import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../components/ui/card.jsx";
import { Label } from "../components/ui/label.jsx";
import { Alert, AlertDescription } from "../components/ui/alert.jsx";
import { Shield, CheckCircle } from 'lucide-react';

export default function CompleteRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'user',
    password: '',
    confirmPassword: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    // Pegar parâmetros da URL
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const role = searchParams.get('role');
    
    if (!email) {
      setError('Link de registro inválido ou expirado.');
      return;
    }
    
    setFormData({
      ...formData,
      email: email || '',
      name: name || '',
      role: role || 'user'
    });
  }, [searchParams]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validações
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Verificar se o email já existe
      try {
        const users = await User.list({ email: formData.email });
        if (users && users.length > 0) {
          setError('Este email já está cadastrado. Faça login ou use a recuperação de senha.');
          setIsLoading(false);
          return;
        }
      } catch (error) {
        // Se não conseguir verificar, continuar (falha não crítica)
        console.warn('Não foi possível verificar emails duplicados:', error);
      }
      
      // Criar o usuário
      // Dependendo da sua implementação, isso pode ser um outro endpoint ou uma chamada específica
      // Aqui estou simulando o registro através da API
      
      /* 
      // Exemplo de criação de usuário (ajuste conforme sua implementação)
      await User.create({
        email: formData.email,
        full_name: formData.name,
        role: formData.role,
        password: formData.password // em um sistema real, nunca envie senhas em texto puro
      });
      */
      
      // Como a plataforma Base44 gerencia autenticação, provavelmente você só precisará atualizar metadados
      // Simulação de sucesso
      setSuccess(true);
      
      // Redirect após alguns segundos
      setTimeout(() => {
        navigate(createPageUrl('AdminLogin'));
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao completar registro:', error);
      setError('Ocorreu um erro ao processar seu registro. Tente novamente ou entre em contato com o suporte.');
    }
    
    setIsLoading(false);
  };
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Registro Concluído!</CardTitle>
            <CardDescription>
              Sua conta foi criada com sucesso.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="mb-4">Você será redirecionado para a página de login em alguns instantes.</p>
            <Button
              className="w-full bg-primary"
              onClick={() => navigate(createPageUrl('AdminLogin'))}
            >
              Ir para o Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 text-primary">
            <Shield className="h-6 w-6" />
            <CardTitle>Complete seu Cadastro</CardTitle>
          </div>
          <CardDescription>
            Defina sua senha para acessar o HelpDesk Pro
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                readOnly
                disabled
                className="bg-gray-50"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Nome Completo</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirme a Senha</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Processando...' : 'Completar Cadastro'}
            </Button>
          </form>
        </CardContent>
        <CardFooter>
          <div className="w-full text-center">
            <p className="text-sm text-muted-foreground">
              Já tem uma conta?{' '}
              <a 
                href={createPageUrl('AdminLogin')}
                className="text-primary hover:underline"
              >
                Faça Login
              </a>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}